import React, {
  useState, useEffect, useCallback, useRef,
} from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Button from '../../../../shared/_TBComponents/Button';
import Icon from '../../../../shared/_TBComponents/Icon';
import Text from '../../../../shared/_TBComponents/Text';
import {
  buildExportFilename, exportRowsToCSV, exportTableToFile,
} from '../../../../shared/utils/tableExport';
import ExportDataModal from '../../../navigator/components/presentational/modals/ExportDataModal';
import ConfirmModal from '../../../navigator/components/presentational/modals/ConfirmModal';
import EasyJoinTable from './EasyJoinTable';
import EasyJoinModal from './EasyJoinModal';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #F5F5F5;
  overflow: hidden;
`;

const TopSection = styled.div`
  background: #FFF;
  padding: 16px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`;

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
`;

const BackLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #77A7FF;
  font-size: 16px;
  font-weight: 700;
  font-family: inherit;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ActionButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  flex-shrink: 0;

  @media (max-width: 767px) {
    width: 100%;

    button {
      flex: 1;
    }
  }
`;

const BottomSection = styled.div`
  flex: 1;
  min-height: 0;
  background: #FFF;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const HiddenTable = styled.table`
  display: none;
`;

const IntroBanner = styled.div`
  margin-bottom: 16px;
`;

const formatCode = (code) => {
  if (!code?.joinCode) return '-';
  const s = code.joinCode.replace(/-/g, '');
  const m = Math.ceil(s.length / 2);
  return `${s.slice(0, m)}-${s.slice(m)}`;
};

const EasyJoinPage = ({
  codes,
  calendars,
  groups,
  isLoadingCodes,
  isCreating,
  isUpdating,
  isDeleting,
  lastCreated,
  lastUpdated,
  onBack,
  backLabel,
  onCreate,
  onUpdate,
  onDelete,
  onClearLastCreated,
  onClearLastUpdated,
}) => {
  const [editingCode, setEditingCode] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [codeToDelete, setCodeToDelete] = useState(null);
  const [bulkAction, setBulkAction] = useState(null);
  const [bulkSelectedIds, setBulkSelectedIds] = useState([]);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [visibleCodes, setVisibleCodes] = useState(codes);
  const [newCodeId, setNewCodeId] = useState(null);
  const tableRef = useRef(null);

  useEffect(() => {
    if (lastCreated) {
      setIsModalOpen(false);
      setEditingCode(null);
      setNewCodeId(lastCreated.id);
      tableRef.current?.scrollToCode(lastCreated.id);
      onClearLastCreated();
    }
  }, [lastCreated]);

  useEffect(() => {
    if (lastUpdated) {
      setIsModalOpen(false);
      setEditingCode(null);
      onClearLastUpdated();
    }
  }, [lastUpdated]);

  const handleOpenCreate = () => {
    setEditingCode(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setEditingCode(null);
    setIsModalOpen(false);
  };

  const handleEdit = useCallback((code) => {
    setEditingCode(code);
    setIsModalOpen(true);
  }, []);

  const handleBulkAction = (action, ids) => {
    setBulkAction(action);
    setBulkSelectedIds(ids);
  };

  const confirmBulkDelete = () => {
    onDelete(bulkSelectedIds);
    setBulkAction(null);
    setBulkSelectedIds([]);
  };

  const confirmSingleDelete = () => {
    onDelete([codeToDelete.id]);
    setCodeToDelete(null);
  };

  const exportToXLSX = () => {
    exportTableToFile('easy-join-export-table', buildExportFilename('easy-join-codes', 'xlsx'), 'EasyJoinCodes');
    setIsExportOpen(false);
  };

  const exportToXLS = () => {
    exportTableToFile('easy-join-export-table', buildExportFilename('easy-join-codes', 'xls'), 'EasyJoinCodes');
    setIsExportOpen(false);
  };

  const exportToCSV = () => {
    const headers = ['Name', 'Code', 'Password', 'Groups', 'Calendar', 'Valid Until'];
    const rows = visibleCodes.map((c) => [
      c.description || '',
      formatCode(c),
      c.joinPassword ? c.joinPassword.replace(/-/g, '') : '',
      (c.groups || []).map((g) => g.name).join(', '),
      c.calendar?.name || '',
      c.expirationDate || '',
    ]);
    exportRowsToCSV(headers, rows, buildExportFilename('easy-join-codes', 'csv'));
    setIsExportOpen(false);
  };

  return (
    <Container>
      <TopSection>
        <TitleGroup>
          {onBack && (
            <BackLink type='button' onClick={onBack}>
              <Icon icon='left-arrow' size={12} color='#77A7FF' />
              {backLabel || 'Back'}
            </BackLink>
          )}
          <Text styledTextProps={{ fontSize: '24px', fontWeight: 900, color: '#1A1A1A' }}>
            Easy Join
          </Text>
        </TitleGroup>
        <ActionButtonGroup>
          <Button
            name='Export'
            icon='download'
            iconColor='#1A1A1A'
            customBorderRadius='4px'
            borderColor='#D6D6D6'
            txtColor='#1A1A1A'
            bgColor='var(--theme-bg-surface, #FFF)'
            styledTextProps={{ fontSize: '14px', fontWeight: 700, letterSpacing: '0.3px' }}
            styledWrapperProps={{ boxShadow: '0 1px 3px 0 rgba(48, 49, 51, 0.10), 0 0 1px 0 rgba(48, 49, 51, 0.05)' }}
            onClick={() => setIsExportOpen(true)}
          />
          <Button
            name='Create Easy Join Code'
            icon='plus'
            iconColor='#FFF'
            txtColor='#FFF'
            bgColor='#1A1A1A'
            borderColor='#1A1A1A'
            customBorderRadius='4px'
            styledTextProps={{ fontSize: '14px', fontWeight: 700, letterSpacing: '0.3px' }}
            styledWrapperProps={{ boxShadow: '0 1px 3px 0 rgba(48, 49, 51, 0.10), 0 0 1px 0 rgba(48, 49, 51, 0.05)' }}
            onClick={handleOpenCreate}
          />
        </ActionButtonGroup>
      </TopSection>

      <BottomSection>
        <EasyJoinTable
          ref={tableRef}
          codes={codes}
          isLoadingCodes={isLoadingCodes}
          onEdit={handleEdit}
          onDeleteCode={setCodeToDelete}
          onBulkAction={handleBulkAction}
          onVisibleCodesChange={setVisibleCodes}
          newCodeId={newCodeId}
          bannerContent={(
            <IntroBanner>
              <Text
                styledTextProps={{ fontSize: '14px', color: '#1A1A1A' }}
                styledWrapperProps={{ marginBottom: '4px' }}
              >
                Easy Join codes make it easy for your athletes to create accounts and start
                using the app.
              </Text>
              <Text styledTextProps={{ fontSize: '14px', color: '#1A1A1A' }}>
                When you create an Easy Join code, you can set the calendar/groups the athletes
                should be assigned to. Then, just give them the code and tell them to enter it
                into the app to get started.
              </Text>
            </IntroBanner>
          )}
        />
      </BottomSection>

      {/* Add/Edit Code Modal */}
      <EasyJoinModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        editingCode={editingCode}
        groups={groups}
        calendars={calendars}
        isCreating={isCreating}
        isUpdating={isUpdating}
        onCreate={onCreate}
        onUpdate={onUpdate}
      />

      <ConfirmModal
        isOpen={!!codeToDelete}
        onClose={() => setCodeToDelete(null)}
        onConfirm={confirmSingleDelete}
        title='Delete Easy Join Code'
        message={`Are you sure you want to delete the code ${formatCode(codeToDelete)}? This action cannot be undone.`}
        isLoading={isDeleting}
      />

      <ConfirmModal
        isOpen={bulkAction === 'deleteCodes'}
        onClose={() => { setBulkAction(null); setBulkSelectedIds([]); }}
        onConfirm={confirmBulkDelete}
        title={`Delete ${bulkSelectedIds.length} Easy Join Code${bulkSelectedIds.length === 1 ? '' : 's'}`}
        message='Are you sure you want to delete these Easy Join codes? Athletes will no longer be able to use them to join.'
        isLoading={isDeleting}
      />

      <ExportDataModal
        isOpen={isExportOpen}
        onClose={() => setIsExportOpen(false)}
        onExportXLSX={exportToXLSX}
        onExportXLS={exportToXLS}
        onExportCSV={exportToCSV}
      />

      <HiddenTable id='easy-join-export-table'>
        <thead>
          <tr>
            <th>Name</th>
            <th>Code</th>
            <th>Password</th>
            <th>Groups</th>
            <th>Calendar</th>
            <th>Valid Until</th>
          </tr>
        </thead>
        <tbody>
          {visibleCodes.map((c) => (
            <tr key={c.id}>
              <td>{c.description || ''}</td>
              <td>{formatCode(c)}</td>
              <td>{c.joinPassword ? c.joinPassword.replace(/-/g, '') : ''}</td>
              <td>{(c.groups || []).map((g) => g.name).join(', ')}</td>
              <td>{c.calendar?.name || ''}</td>
              <td>{c.expirationDate || ''}</td>
            </tr>
          ))}
        </tbody>
      </HiddenTable>
    </Container>
  );
};

EasyJoinPage.propTypes = {
  codes: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    joinCode: PropTypes.string,
    joinPassword: PropTypes.string,
    description: PropTypes.string,
    expirationDate: PropTypes.string,
    startDate: PropTypes.number,
    endDate: PropTypes.number,
    dateOffset: PropTypes.number,
    groups: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    })),
    calendar: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
  })).isRequired,
  calendars: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.number, name: PropTypes.string }),
  ).isRequired,
  groups: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.number, name: PropTypes.string }),
  ).isRequired,
  isLoadingCodes: PropTypes.bool.isRequired,
  isCreating: PropTypes.bool.isRequired,
  isUpdating: PropTypes.bool.isRequired,
  isDeleting: PropTypes.bool.isRequired,
  lastCreated: PropTypes.shape({
    id: PropTypes.number,
    joinCode: PropTypes.string,
    joinPassword: PropTypes.string,
    description: PropTypes.string,
    expirationDate: PropTypes.string,
    groups: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })),
    calendar: PropTypes.shape({ id: PropTypes.number, name: PropTypes.string }),
  }),
  onBack: PropTypes.func,
  backLabel: PropTypes.string,
  onCreate: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onClearLastCreated: PropTypes.func.isRequired,
  lastUpdated: PropTypes.shape({ id: PropTypes.number }),
  onClearLastUpdated: PropTypes.func.isRequired,
};

EasyJoinPage.defaultProps = {
  lastCreated: null,
  lastUpdated: null,
  onBack: null,
  backLabel: null,
};

export default EasyJoinPage;
